// The sea's waves, computed on the GPU: a wind-driven wave spectrum evolved in time and turned into slope maps with an FFT (Tessendorf's method).
// Human-facing only, like waterView.ts, which draws the result. Nothing here feeds back into the simulation.
//
// What it makes
//   Three tiles of sea (CASCADES), each a repeating square that is a sum of thousands of travelling waves. Together they cover wavelengths from
//   about 4 m to 560 m (tile 0), 0.7 m to 90 m (tile 1) and 0.12 m to 14 m (tile 2). Every tile is stored as two mip-mapped textures:
//     A: slope x, slope z, height, Jacobian of the horizontal displacement (below 1 where the water piles up and folds: whitecaps)
//     B: slope x squared, slope z squared, slope x times slope z, and the foam left by folding crests (it fades with time)
//   The second texture is what removes the grain from far water. A mip level holds the average slope of the waves it can resolve, and the
//   average of the squares tells how much slope the level has lost; the shader turns that lost slope into roughness (a broader, softer reflection),
//   so the sea goes smooth and glossy toward the horizon instead of striping. The old sines had no such filter.
//
// The spectrum (the sea state)
//   JONSWAP in frequency (a growing sea with a given fetch) with the TMA correction for shallow water (waves lose their long components in a few
//   metres of water, which is what this world's shelf is), spread over direction with the Mitsuyasu/Hasselmann cos^2s law, so the sea is peaked
//   along the wind near the peak frequency and nearly isotropic for the ripples. Deep or shallow, the dispersion is omega^2 = g k tanh(k d).
//   The caller gives wind speed, wind direction, fetch and depth; the spectrum is rebuilt when they change (cheap: three small draws).
//   The random numbers are a fixed function of the wave vector, so when the sea state changes the waves grow, shrink and turn in place.
//
// The FFT
//   Eight real fields (height, slopes, horizontal displacement and its three derivatives) are packed two to a complex number, four complex
//   numbers to two textures, and each texture goes through an inverse FFT.
//   The cost of the transform on the CPU side of the browser is the number of passes (every pass is a render-target switch and a draw), not the
//   arithmetic, so the passes are kept few: the tiles sit side by side in one wide atlas (one pass covers all of them), the two packed spectra
//   travel together (multiple render targets: one pass writes both), and the butterflies are radix 4 (a Stockham autosort FFT: four stages per
//   axis at 256^2, no bit reversal, the twiddles read from a small table). One frame is 1 evolve pass, 8 FFT passes and 2 assemble passes per
//   tile, 15 draws for three tiles, where the first version used 108 (separate tiles, separate packs, radix 2).
//   Float32 render targets are required (EXT_color_buffer_float); `OceanFFT.supported(renderer)` says whether they exist.
//
// Time: the phase of every wave is omega t with omega rounded to a multiple of OMEGA0, so t can be wrapped at 2 pi / OMEGA0 (628 s) with no
// jump and no loss of precision in a long session. (The whole sea repeats exactly every 10.5 minutes; no one can see it.)
import * as THREE from 'three';

export const G = 9.81;
export const OMEGA0 = 0.01;
export const T_WRAP = 2 * Math.PI / OMEGA0;

/** Wave state of the sea. `dir`: the direction the waves travel toward, radians in the three x-z plane (atan2(z, x)). */
export interface SeaState {
  speed: number;     // wind speed at 10 m, m/s
  dir: number;       // radians
  fetch: number;     // metres of open water the wind has blown over (a bay: some kilometres)
  depth: number;     // the sea's depth away from the beach, metres
  swell?: SwellTrain[];   // swell from far storms (at most MAX_SWELL trains), on top of the wind sea; absent or empty: none
}

/** One train of swell: significant height (m), peak period (s) and the direction it travels toward (radians in the three x-z plane, like SeaState.dir). */
export interface SwellTrain { hs: number; tp: number; dir: number; }
export const MAX_SWELL = 2;

export const DEFAULT_SEA: SeaState = { speed: 6.5, dir: -0.64, fetch: 100000, depth: 3 };   // fetch: the server's sea_state.DEFAULT_FETCH

/** Tiles: side length in metres and the wave numbers (rad/m) each carries. Band edges are crossfaded in log k so that energy adds up to the spectrum's. */
export const CASCADE_L = [563.0, 87.3, 13.7];
const BAND_EDGE = [0.90, 6.0];       // k where tile 0 hands over to tile 1, and tile 1 to tile 2
const BAND_WIDTH = 0.30;             // half-width of the crossfade, in ln k
const K_TOP = 52;                    // spectrum ends here (tile 2's Nyquist is 58)

// ---------------------------------------------------------------------------------------------------------------------------------------------
// The same spectrum on the CPU: for the shore model (wave height and period) and to test the GPU against.
// ---------------------------------------------------------------------------------------------------------------------------------------------
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** Weight of tile c at wave number k (squares sum to 1 over the tiles; 0 beyond K_TOP). */
export function bandWeight(c: number, k: number): number {
  if (k <= 0 || k >= K_TOP * 1.15) return 0;
  const lk = Math.log(k);
  const t1 = smooth(Math.log(BAND_EDGE[0]) - BAND_WIDTH, Math.log(BAND_EDGE[0]) + BAND_WIDTH, lk);
  const t2 = smooth(Math.log(BAND_EDGE[1]) - BAND_WIDTH, Math.log(BAND_EDGE[1]) + BAND_WIDTH, lk);
  const cut = 1 - smooth(Math.log(K_TOP * 0.75), Math.log(K_TOP * 1.15), lk);       // soft end of the spectrum
  const a = Math.PI / 2;
  const w = c === 0 ? Math.cos(a * t1) : c === 1 ? Math.sin(a * t1) * Math.cos(a * t2) : Math.sin(a * t2);
  return w * (c === 2 ? cut : 1);
}

/** Peak angular frequency of a fetch-limited sea (rad/s), not below the fully developed value. */
export function peakOmega(s: SeaState): number {
  const u = Math.max(s.speed, 0.5);
  const fl = 22 * Math.pow(G * G / (u * Math.max(s.fetch, 100)), 1 / 3);
  return Math.max(fl, 0.877 * G / u);
}

/** Angular frequency of a wave of wave number k in depth d. */
export function omegaOf(k: number, d: number): number { return Math.sqrt(G * k * Math.tanh(Math.min(k * Math.max(d, 0.05), 15))); }

/** JONSWAP frequency spectrum with the TMA depth factor: variance per rad/s. */
export function jonswap(w: number, s: SeaState): number {
  const u = Math.max(s.speed, 0.5);
  const wp = peakOmega(s);
  const alpha = Math.min(0.0081, 0.076 * Math.pow(G * Math.max(s.fetch, 100) / (u * u), -0.22));
  const sig = w <= wp ? 0.07 : 0.09;
  const r = Math.exp(-((w - wp) * (w - wp)) / (2 * sig * sig * wp * wp));
  let S = alpha * G * G * Math.pow(w, -5) * Math.exp(-1.25 * Math.pow(wp / w, 4)) * Math.pow(3.3, r);
  const wh = w * Math.sqrt(Math.max(s.depth, 0.05) / G);
  const phi = wh <= 1 ? 0.5 * wh * wh : wh < 2 ? 1 - 0.5 * (2 - wh) * (2 - wh) : 1;
  return S * phi;
}

/** Directional spread: cos^2s of half the angle off the wind, normalised to integrate to 1 round the circle. */
export function spread(w: number, dTheta: number, s: SeaState): number {
  const wp = peakOmega(s), x = w / wp;
  const sp = Math.min(40, Math.max(1.5, x < 1.05 ? 6.97 * Math.pow(x, 4.06) : 9.77 * Math.pow(x, -2.5)));
  const c = Math.cos(0.5 * dTheta);
  return Math.pow(Math.max(c, 0), 2 * sp) * Math.sqrt(sp + 0.25) / (2 * Math.sqrt(Math.PI));
}

/** Wrap an angle to (-pi, pi]. */
const wrapPi = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

// Swell: waves a far storm made, run across the ocean. Narrow in frequency (a Gaussian about the peak) and in direction (cos^2s with a large s), with the variance (Hs/4)^2 the server
// gave. It is not a JONSWAP sea and has no TMA factor: it is not made by the local wind. The three tiles carry it as they carry the sea (it lies in tile 0).
export const SWELL_SIGMA = 0.12;       // width of the swell's frequency band, over its peak frequency
export const SWELL_S = 24;             // directional exponent s of the swell (cos^2s of half the angle off its heading): about 10 degrees wide

export interface SwellParams { wp: number; sig: number; dir: number; sp: number; variance: number; }

const swellDir = (dTheta: number, sp: number) => {
  const c = Math.cos(0.5 * wrapPi(dTheta));
  return Math.pow(Math.max(c, 0), 2 * sp) * Math.sqrt(sp + 0.25) / (2 * Math.sqrt(Math.PI));
};

/** The wave number whose angular frequency is w, in depth d (bisection). */
function waveNumber(w: number, d: number): number {
  let lo = 0, hi = 4;
  for (let i = 0; i < 40; i++) { const mid = 0.5 * (lo + hi); if (omegaOf(mid, d) < w) lo = mid; else hi = mid; }
  return 0.5 * (lo + hi);
}

/**
 * The swell of a sea state as the numbers the spectrum needs. The grid of tile 0 has only a few wave vectors inside a swell's narrow band, so summing the band over it would give a
 * height that jumps about as the period or direction moves: the swell's variance is scaled so that the grid sum is exactly (Hs/4)^2. (It costs a few hundred modes.)
 */
export function swellParams(s: SeaState): SwellParams[] {
  const out: SwellParams[] = [];
  const dk = 2 * Math.PI / CASCADE_L[0]!;
  for (const t of (s.swell ?? []).slice(0, MAX_SWELL)) {
    if (!(t.hs > 0.005) || !(t.tp > 2)) continue;
    const wp = 2 * Math.PI / t.tp, sig = SWELL_SIGMA * wp;
    const p: SwellParams = { wp, sig, dir: t.dir, sp: SWELL_S, variance: 0 };
    const kmax = waveNumber(wp + 5 * sig, s.depth), m = Math.ceil(kmax / dk) + 1;
    let sum = 0;
    for (let j = -m; j <= m; j++) for (let i = -m; i <= m; i++) sum += swellMode(i * dk, j * dk, dk, s.depth, { ...p, variance: 1 });
    const target = (t.hs / 4) * (t.hs / 4);
    p.variance = sum > 0 ? target / sum : 0;
    if (p.variance > 0) out.push(p);
  }
  return out;
}

/** Variance one swell train puts into the mode at (kx, kz): the Gaussian frequency band times the directional spread, in the same measure as the wind sea's. */
function swellMode(kx: number, kz: number, dk: number, depth: number, p: SwellParams): number {
  const k = Math.hypot(kx, kz);
  if (k < 1e-6) return 0;
  const th = Math.tanh(k * depth), w = Math.sqrt(G * k * th);
  const x = (w - p.wp) / p.sig;
  if (Math.abs(x) > 6) return 0;
  const dw = G / (2 * w) * (th + k * depth * (1 - th * th));
  const S = p.variance / (Math.sqrt(2 * Math.PI) * p.sig) * Math.exp(-0.5 * x * x);
  return S * swellDir(Math.atan2(kz, kx) - p.dir, p.sp) * dw / k * dk * dk;
}

/** Variance of one mode (the squared amplitude |h(k)|^2 the spectrum puts at wave vector (kx, kz)), before the tile's band weight. `swell`: swellParams(s), worked out once by a caller that loops. */
export function modeVariance(kx: number, kz: number, dk: number, s: SeaState, rippleGain: number, swell?: SwellParams[]): number {
  const k = Math.hypot(kx, kz);
  if (k < 1e-6) return 0;
  const th = Math.tanh(k * s.depth), w = Math.sqrt(G * k * th);
  const dw = G / (2 * w) * (th + k * s.depth * (1 - th * th));
  const dTheta = wrapPi(Math.atan2(kz, kx) - s.dir);       // wrapped: the waves heading across the +-pi cut have their share
  // Extra energy in the short gravity-capillary waves (a pure omega^-5 tail has about half the slope the sea really has: Cox and Munk).
  const rip = 1 + rippleGain * smooth(2.0, 12.0, k);
  let v = jonswap(w, s) * spread(w, dTheta, s) * dw / k * dk * dk * rip;
  for (const p of swell ?? swellParams(s)) v += swellMode(kx, kz, dk, s.depth, p);
  return v;
}

/** Whether two swell lists differ enough to rebuild the spectrum. */
export function swellMoved(a: SwellTrain[] | undefined, b: SwellTrain[] | undefined): boolean {
  const n = Math.min(MAX_SWELL, a?.length ?? 0), m = Math.min(MAX_SWELL, b?.length ?? 0);
  if (n !== m) return true;
  for (let i = 0; i < n; i++) {
    const x = a![i]!, y = b![i]!;
    if (Math.abs(x.hs - y.hs) > 0.01 || Math.abs(x.tp - y.tp) > 0.05 || Math.abs(wrapPi(x.dir - y.dir)) > 0.002) return true;
  }
  return false;
}

export interface SeaStats {
  hs: number;        // significant wave height of the whole spectrum (4 sigma), metres
  hsLong: number;    // ... of the part carried by tile 0 (the organised swell and sea that reach the beach)
  tp: number;        // peak period, s
  mss: number;       // total mean square slope
  sigma: number;     // rms height
}

export const RIPPLE_GAIN = 2.2;

/**
 * The statistics of a sea state, summed over the same wave-vector grids the GPU uses, as a job that can be run a slice at a time.
 * The whole sum is about 195 000 modes (three 255 x 255 grids) and costs some 50 ms of JavaScript, far too much for one frame, and the
 * sea state moves a little every frame while the wind relaxes, so the renderer runs the job in slices of about a millisecond and starts a
 * new one only when the state has moved. The sum runs in the same order as before, so a finished job gives exactly the old numbers.
 */
export class SeaStatsJob {
  readonly state: SeaState;
  private readonly n: number;
  private c = 0; private j: number;
  private m0 = 0; private m0long = 0; private mss = 0;
  private done = false;
  private readonly sw: SwellParams[];
  constructor(s: SeaState, n = 256) { this.state = { ...s, ...(s.swell ? { swell: s.swell.map(t => ({ ...t })) } : {}) }; this.n = n; this.j = -n / 2 + 1; this.sw = swellParams(this.state); }
  get finished() { return this.done; }
  /** Work for about `ms` milliseconds (at least one row of modes), or to the end when `ms` is Infinity. Returns true when the job is finished. */
  run(ms: number): boolean {
    if (this.done) return true;
    const n = this.n, s = this.state, t0 = ms === Infinity ? 0 : performance.now();
    while (this.c < CASCADE_L.length) {
      const L = CASCADE_L[this.c]!, dk = 2 * Math.PI / L;
      for (; this.j < n / 2; this.j++) {
        const kz = this.j * dk;
        for (let i = -n / 2 + 1; i < n / 2; i++) {
          const kx = i * dk, k = Math.hypot(kx, kz);
          const wb = bandWeight(this.c, k);
          if (wb <= 0) continue;
          const v = modeVariance(kx, kz, dk, s, RIPPLE_GAIN, this.sw) * wb * wb;
          this.m0 += v; this.mss += v * k * k;
          if (this.c === 0) this.m0long += v;
        }
        if (ms !== Infinity && performance.now() - t0 >= ms) { this.j++; return this.advance(); }
      }
      this.c++; this.j = -n / 2 + 1;
    }
    this.done = true;
    return true;
  }
  private advance(): boolean {
    if (this.j >= this.n / 2) { this.c++; this.j = -this.n / 2 + 1; }
    if (this.c >= CASCADE_L.length) { this.done = true; return true; }
    return false;
  }
  /** The statistics (valid once finished). */
  get result(): SeaStats {
    const s = this.state;
    return { hs: 4 * Math.sqrt(this.m0), hsLong: 4 * Math.sqrt(this.m0long), tp: 2 * Math.PI / peakOmega(s), mss: this.mss, sigma: Math.sqrt(this.m0) };
  }
}

/** Expected height and slope statistics of a sea state, all at once (about 50 ms; the renderer uses SeaStatsJob in slices instead). */
export function seaStats(s: SeaState, n = 256): SeaStats {
  const job = new SeaStatsJob(s, n);
  job.run(Infinity);
  return job.result;
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// FFT plumbing
// ---------------------------------------------------------------------------------------------------------------------------------------------

/** Butterfly table for an N-point inverse FFT: one row of N entries per stage, each (twiddle re, twiddle im, source a, source b). The first stage
 *  reads the input in bit-reversed order, so the passes need no separate reordering. Output = in[a] + w * in[b]. Exported for the tests. */
export function buildButterfly(n: number): Float32Array {
  const stages = Math.log2(n) | 0;
  const data = new Float32Array(stages * n * 4);     // layout: [pos * stages + stage] so a (stages x n) texture reads (stage, pos)
  const rev = (x: number) => { let r = 0; for (let b = 0; b < stages; b++) { r = (r << 1) | ((x >> b) & 1); } return r; };
  for (let s = 0; s < stages; s++) {
    const span = 1 << s;
    for (let x = 0; x < n; x++) {
      const kk = (x * (n >> (s + 1))) % n;
      const ang = 2 * Math.PI * kk / n;              // inverse transform: positive exponent
      const top = (x % (2 * span)) < span;
      let a: number, b: number;
      if (s === 0) { if (top) { a = rev(x); b = rev(x + 1); } else { a = rev(x - 1); b = rev(x); } }
      else { if (top) { a = x; b = x + span; } else { a = x - span; b = x; } }
      const o = (x * stages + s) * 4;
      data[o] = Math.cos(ang); data[o + 1] = Math.sin(ang); data[o + 2] = a; data[o + 3] = b;
    }
  }
  return data;
}

/** The whole 1-D transform on the CPU with the same table (for the tests). `re`, `im` are modified in place. */
export function ifft1d(re: Float64Array, im: Float64Array, table: Float32Array): void {
  const n = re.length, stages = Math.log2(n) | 0;
  let ar = Float64Array.from(re), ai = Float64Array.from(im);
  let br = new Float64Array(n), bi = new Float64Array(n);
  for (let s = 0; s < stages; s++) {
    for (let x = 0; x < n; x++) {
      const o = (x * stages + s) * 4, wr = table[o], wi = table[o + 1], a = table[o + 2], b = table[o + 3];
      br[x] = ar[a] + wr * ar[b] - wi * ai[b];
      bi[x] = ai[a] + wr * ai[b] + wi * ar[b];
    }
    [ar, br] = [br, ar]; [ai, bi] = [bi, ai];
  }
  re.set(ar); im.set(ai);
}

/** Radices of the Stockham FFT of length n: fours, then a two when n is twice a power of four (256 -> 4,4,4,4; 128 -> 4,4,4,2). */
export function fftRadices(n: number): number[] {
  const out: number[] = [];
  let rem = n;
  while (rem > 1) {
    if (rem % 4 === 0) { out.push(4); rem /= 4; }
    else if (rem % 2 === 0) { out.push(2); rem /= 2; }
    else throw new Error('fft length must be a power of two');
  }
  return out;
}

/** Twiddle table for the Stockham passes: texel m holds exp(+2 pi i m / n) as (cos, sin, 0, 0). Computed in double precision, so the GPU never evaluates a sine. */
export function buildTwiddles(n: number) {
  const t = new Float32Array(n * 4);
  for (let m = 0; m < n; m++) { const a = 2 * Math.PI * m / n; t[m * 4] = Math.cos(a); t[m * 4 + 1] = Math.sin(a); }
  return t;
}

/** The inverse transform (positive exponent, no 1/n) by the same passes the GPU runs, one output sample at a time, for the tests. `re`, `im` are modified in place.
 *  Pass with span ns and radix r: output i takes digit d = (i / ns) mod r, base j = floor(i / (ns r)) ns + i mod ns, and sums x[j + q n/r] exp(+2 pi i q (i mod ns r) / (ns r)) over q. */
export function ifft1dStockham(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  let ar = Float64Array.from(re), ai = Float64Array.from(im);
  let br = new Float64Array(n), bi = new Float64Array(n);
  let ns = 1;
  for (const r of fftRadices(n)) {
    const stride = n / r, span = ns * r;
    for (let i = 0; i < n; i++) {
      const j = Math.floor(i / span) * ns + (i % ns), part = i % span;
      let sr = 0, si = 0;
      for (let q = 0; q < r; q++) {
        const ang = 2 * Math.PI * q * part / span, c = Math.cos(ang), s = Math.sin(ang);
        const xr = ar[j + q * stride]!, xi = ai[j + q * stride]!;
        sr += xr * c - xi * s; si += xr * s + xi * c;
      }
      br[i] = sr; bi[i] = si;
    }
    [ar, br] = [br, ar]; [ai, bi] = [bi, ai];
    ns = span;
  }
  re.set(ar); im.set(ai);
}

const VERT = /* glsl */`
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const COMMON = /* glsl */`
const float G = ${G.toFixed(2)};
const float PI = 3.14159265358979;
const float OMEGA0 = ${OMEGA0};
vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
`;

// Spectrum -> initial amplitudes h0(k) and conj(h0(-k)) for one tile. Mirrors modeVariance() above.
const H0_FRAG = /* glsl */`
precision highp float; precision highp int;
varying vec2 vUv;
${COMMON}
uniform float uN, uSpeed, uDir, uFetch, uDepth, uRipple;
uniform float uLs[3];         // tile side lengths, m
uniform float uGains[3];      // per-tile gain
uniform vec2 uEdge;           // band edges (k)
uniform float uWidth, uKTop;
uniform int uSwN;              // swell trains in use (0 to 2)
uniform vec4 uSwA[2];          // per train: peak omega, sigma omega, heading (rad), directional exponent s
uniform float uSwV[2];         // per train: variance, scaled so the tile's grid sums to (Hs/4)^2 (swellParams)

uint pcg(uint v) { uint st = v * 747796405u + 2891336453u; uint w = ((st >> ((st >> 28u) + 4u)) ^ st) * 277803737u; return (w >> 22u) ^ w; }
float u01(uint h) { return (float(h >> 8u) + 0.5) / 16777216.0; }
vec2 gauss(ivec2 m, uint seed) {
  uint key = pcg(uint(m.x + 8192) * 16384u + uint(m.y + 8192) + seed * 2654435761u);
  float u1 = u01(pcg(key ^ 0x9e3779b9u)), u2 = u01(pcg(key ^ 0x85ebca6bu));
  float r = sqrt(-2.0 * log(u1));
  return r * vec2(cos(2.0 * PI * u2), sin(2.0 * PI * u2));
}
float smooth01(float a, float b, float x) { float t = clamp((x - a) / (b - a), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }

float bandWeight(int c, float k) {
  if (k <= 0.0 || k >= uKTop * 1.15) return 0.0;
  float lk = log(k);
  float t1 = smooth01(log(uEdge.x) - uWidth, log(uEdge.x) + uWidth, lk);
  float t2 = smooth01(log(uEdge.y) - uWidth, log(uEdge.y) + uWidth, lk);
  float cut = 1.0 - smooth01(log(uKTop * 0.75), log(uKTop * 1.15), lk);
  float a = 0.5 * PI;
  float w = c == 0 ? cos(a * t1) : (c == 1 ? sin(a * t1) * cos(a * t2) : sin(a * t2));
  return w * (c == 2 ? cut : 1.0);
}
float peakOmega() {
  float u = max(uSpeed, 0.5);
  float fl = 22.0 * pow(G * G / (u * max(uFetch, 100.0)), 1.0 / 3.0);
  return max(fl, 0.877 * G / u);
}
float jonswap(float w) {
  float u = max(uSpeed, 0.5), wp = peakOmega();
  float alpha = min(0.0081, 0.076 * pow(G * max(uFetch, 100.0) / (u * u), -0.22));
  float sig = w <= wp ? 0.07 : 0.09;
  float r = exp(-((w - wp) * (w - wp)) / (2.0 * sig * sig * wp * wp));
  float S = alpha * G * G * pow(w, -5.0) * exp(-1.25 * pow(wp / w, 4.0)) * pow(3.3, r);
  float wh = w * sqrt(max(uDepth, 0.05) / G);
  float phi = wh <= 1.0 ? 0.5 * wh * wh : (wh < 2.0 ? 1.0 - 0.5 * (2.0 - wh) * (2.0 - wh) : 1.0);
  return S * phi;
}
float spreadFn(float w, float dTheta) {
  float wp = peakOmega(), x = w / wp;
  float sp = clamp(x < 1.05 ? 6.97 * pow(x, 4.06) : 9.77 * pow(x, -2.5), 1.5, 40.0);
  float c = cos(0.5 * dTheta);
  return pow(max(c, 0.0), 2.0 * sp) * sqrt(sp + 0.25) / (2.0 * sqrt(PI));
}
// amplitude |h0| scale at wave vector k (already divided by the two sqrt(2)s: h0 = (xi_r + i xi_i) * this)
float amp(vec2 k, float dk, int cascade, float gain) {
  float kl = length(k);
  if (kl < 1e-6) return 0.0;
  float th = tanh(min(kl * uDepth, 15.0)), w = sqrt(G * kl * th);
  float dw = G / (2.0 * w) * (th + kl * uDepth * (1.0 - th * th));
  float dTheta = atan(sin(atan(k.y, k.x) - uDir), cos(atan(k.y, k.x) - uDir));
  float rip = 1.0 + uRipple * smooth01(2.0, 12.0, kl);
  float v = jonswap(w) * spreadFn(w, dTheta) * dw / kl * dk * dk * rip;
  for (int i = 0; i < 2; i++) {                      // swell trains (mirror of swellMode)
    if (i >= uSwN) break;
    vec4 a = uSwA[i];
    float x = (w - a.x) / a.y;
    if (abs(x) > 6.0) continue;
    float sw = uSwV[i] / (2.50662827 * a.y) * exp(-0.5 * x * x);
    float dd = atan(k.y, k.x) - a.z;
    float c = cos(0.5 * atan(sin(dd), cos(dd)));
    float d = pow(max(c, 0.0), 2.0 * a.w) * sqrt(a.w + 0.25) / (2.0 * sqrt(PI));
    v += sw * d * dw / kl * dk * dk;
  }
  return 0.5 * sqrt(max(v, 0.0)) * bandWeight(cascade, kl) * gain;
}
void main() {
  ivec2 pa = ivec2(gl_FragCoord.xy);                 // the atlas holds the tiles side by side
  int cascade = pa.x / int(uN);
  ivec2 p = ivec2(pa.x - cascade * int(uN), pa.y);
  ivec2 m = p - ivec2(int(uN) / 2);
  float dk = 2.0 * PI / uLs[cascade];
  if (m.x == -int(uN) / 2 || m.y == -int(uN) / 2) { gl_FragColor = vec4(0.0); return; }     // Nyquist row and column: no partner at -k
  vec2 k = vec2(m) * dk;
  vec2 g1 = gauss(m, uint(cascade) + 1u), g2 = gauss(-m, uint(cascade) + 1u);
  vec2 h0 = g1 * amp(k, dk, cascade, uGains[cascade]);
  vec2 hm = g2 * amp(-k, dk, cascade, uGains[cascade]);
  gl_FragColor = vec4(h0, hm.x, -hm.y);        // h0(k) and conj(h0(-k))
}
`;

// Time step: h(k,t) = h0(k) e^{-i w t} + conj(h0(-k)) e^{+i w t} (with e^{+ik.x} in the transform, a wave at +k then travels toward +k, along the wind);
// then the packed spectra of the fields.
const EVOLVE_FRAG = /* glsl */`
precision highp float; precision highp int;
varying vec2 vUv;
${COMMON}
layout(location = 0) out highp vec4 oPack0;
layout(location = 1) out highp vec4 oPack1;
uniform sampler2D uH0;
uniform float uN, uT, uDepth;
uniform float uLs[3];
void main() {
  ivec2 pa = ivec2(gl_FragCoord.xy);                 // atlas texel; the tile is pa.x / N
  int cascade = pa.x / int(uN);
  ivec2 p = ivec2(pa.x - cascade * int(uN), pa.y);
  vec2 k = vec2(p - ivec2(int(uN) / 2)) * (2.0 * PI / uLs[cascade]);
  float kl = length(k);
  if (kl < 1e-6) { oPack0 = vec4(0.0); oPack1 = vec4(0.0); return; }
  vec4 h0 = texelFetch(uH0, pa, 0);
  float w = sqrt(G * kl * tanh(min(kl * uDepth, 15.0)));
  w = floor(w / OMEGA0 + 0.5) * OMEGA0;
  float c = cos(w * uT), s = sin(w * uT);
  vec2 h = cmul(h0.xy, vec2(c, -s)) + cmul(h0.zw, vec2(c, s));      // e^{-i w t} on h0(k): the wave at +k travels toward +k
  vec2 kn = k / kl;
  // pack 0
  vec2 a1 = (1.0 + kn.x) * h;                                        // height + i * displacement x
  vec2 a2 = vec2(kn.y * h.y - k.x * h.x, -kn.y * h.x - k.x * h.y);  // displacement z + i * slope x
  // pack 1
  vec2 b1 = (k.y + k.x * kn.x) * vec2(-h.y, h.x);                   // slope z + i * dDx/dx
  vec2 b2 = cmul(vec2(k.y * kn.y, k.x * kn.y), h);                  // dDz/dz + i * dDx/dz
  oPack0 = vec4(a1, a2);
  oPack1 = vec4(b1, b2);
}
`;

// One pass of the inverse FFT (Stockham autosort, radix R = 2 or 4) along x or y, inside every tile of the atlas, for both packed spectra at once.
// With ns the span the earlier passes have finished, an output sample at position i along the axis sums the R inputs x[j + q N/R], q = 0..R-1, with
// j = floor(i / (ns R)) ns + i mod ns, each turned by exp(+2 pi i q (i mod ns R) / (ns R)): one table read each (the q = 0 term needs none).
// The first pass reads the evolve pass's natural order and the last leaves natural order, so there is no reordering.
const FFT_FRAG = /* glsl */`
precision highp float; precision highp int;
varying vec2 vUv;
${COMMON}
layout(location = 0) out highp vec4 oPack0;
layout(location = 1) out highp vec4 oPack1;
uniform sampler2D uIn0, uIn1, uTw;
uniform int uN, uNs, uR, uVert;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  int pos = uVert == 1 ? p.y : p.x % uN;             // position along the transform axis, inside the tile
  int x0 = p.x - (p.x % uN);                         // the tile's first column (horizontal passes)
  int span = uNs * uR;
  int j = (pos / span) * uNs + (pos % uNs);
  int stride = uN / uR;
  int part = pos % span;
  int tw = uN / span;
  ivec2 q0 = uVert == 1 ? ivec2(p.x, j) : ivec2(x0 + j, p.y);
  vec4 a = texelFetch(uIn0, q0, 0), b = texelFetch(uIn1, q0, 0);
  vec4 s0 = a, s1 = b;
  for (int q = 1; q < 4; q++) {
    if (q >= uR) break;
    int src = j + q * stride;
    ivec2 pq = uVert == 1 ? ivec2(p.x, src) : ivec2(x0 + src, p.y);
    vec2 w = texelFetch(uTw, ivec2((q * part * tw) % uN, 0), 0).xy;
    vec4 xa = texelFetch(uIn0, pq, 0), xb = texelFetch(uIn1, pq, 0);
    s0 += vec4(cmul(w, xa.xy), cmul(w, xa.zw));
    s1 += vec4(cmul(w, xb.xy), cmul(w, xb.zw));
  }
  oPack0 = s0;
  oPack1 = s1;
}
`;

// FFT results -> the textures the water shader reads.
const ASSEMBLE_FRAG = /* glsl */`
precision highp float; precision highp int;
varying vec2 vUv;
${COMMON}
uniform sampler2D uFA, uFB, uFoam;
uniform int uTileX;                // first column of this tile in the atlas
uniform int uMode;                 // 0: texture A, 1: texture B
uniform float uLambda;             // choppiness (displacement scale; the crest folds where it reaches 1 / the wave's k a)
uniform float uFoamBias, uFoamDecay, uFoamGain, uFoamOn;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  float sg = ((p.x + p.y) & 1) == 0 ? 1.0 : -1.0;
  ivec2 pa = p + ivec2(uTileX, 0);
  vec4 a = texelFetch(uFA, pa, 0) * sg, b = texelFetch(uFB, pa, 0) * sg;
  float eta = a.x, Dz = a.z, Sx = a.w, Sz = b.x, Dxx = b.y, Dzz = b.z, Dxz = b.w;
  float l = -uLambda;
  float jxx = 1.0 + l * Dxx, jzz = 1.0 + l * Dzz;
  float J = jxx * jzz - l * l * Dxz * Dxz;
  vec2 S = vec2(Sx / max(jxx, 0.15), Sz / max(jzz, 0.15));
  if (uMode == 0) {
    gl_FragColor = vec4(S, eta, J);
  } else {
    float prev = texelFetch(uFoam, p, 0).a;
    float src = smoothstep(0.0, 0.30, uFoamBias - J) * uFoamGain * uFoamOn;
    float foam = max(prev * uFoamDecay, src);
    gl_FragColor = vec4(S.x * S.x, S.y * S.y, S.x * S.y, foam);
  }
}
`;

interface Cascade {
  L: number;
  A: THREE.WebGLRenderTarget;
  B: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  flip: number;
}

export interface OceanOptions {
  n?: number;                // texels per side of each tile (power of two)
  cascades?: number;         // 1 to 3 tiles (tile 2 first to go)
}

// A render target with two colour attachments. three r160 has WebGLMultipleRenderTargets; from r162 the same thing is WebGLRenderTarget with `count`
// (and WebGLMultipleRenderTargets is deprecated, later removed), so ask for whichever this build has.
let mrtCount: boolean | null = null;
function makeMrt(w: number, h: number, opts: Record<string, unknown>): THREE.WebGLRenderTarget {
  if (mrtCount === null) {
    const probe = new THREE.WebGLRenderTarget(1, 1, { count: 2 } as any) as any;
    mrtCount = Array.isArray(probe.textures) && probe.textures.length === 2;
    probe.dispose();
  }
  if (mrtCount) return new THREE.WebGLRenderTarget(w, h, { ...opts, count: 2 } as any);
  return new (THREE as any).WebGLMultipleRenderTargets(w, h, 2, opts);
}
function mrtTextures(rt: THREE.WebGLRenderTarget): [THREE.Texture, THREE.Texture] {
  const t = ((rt as any).textures ?? (rt as any).texture) as THREE.Texture[];
  return [t[0]!, t[1]!];
}

export class OceanFFT {
  readonly n: number;
  readonly count: number;
  private readonly casc: Cascade[] = [];
  private readonly h0: THREE.WebGLRenderTarget;                       // the starting spectra of all tiles, side by side (count n wide, n high)
  private readonly pp: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];   // ping-pong pair of two-attachment targets: the two packed spectra, all tiles
  private readonly radices: number[];
  private readonly tw: THREE.DataTexture;
  private readonly quad: THREE.Mesh;
  private readonly scene = new THREE.Scene();
  private readonly cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly mH0: THREE.ShaderMaterial; private readonly mEvolve: THREE.ShaderMaterial;
  private readonly mFFT: THREE.ShaderMaterial; private readonly mAsm: THREE.ShaderMaterial;
  private state: SeaState = { ...DEFAULT_SEA };
  private dirty = true;
  private lastT = -1;
  /** Choppiness: how far the crests lean into each other (also how readily they fold into foam). */
  lambda = 1.0;
  /** Foam threshold: whitecaps start where the surface's Jacobian falls below this. Lower = fewer. The caller sets it from the wind. */
  foamBias = 0.6;
  foamDecay = 5.0;           // seconds for the foam to fade to 1/e
  foamGain = 1.0;
  foamOn = 1.0;
  gain = [1, 1, 1];
  rippleGain = RIPPLE_GAIN;

  /** Whether this context can render to float32 textures (needed for the FFT). */
  static supported(renderer: THREE.WebGLRenderer): boolean {
    const gl = renderer.getContext() as WebGL2RenderingContext;
    return !!(gl && (gl as any).texStorage2D) && !!gl.getExtension('EXT_color_buffer_float');
  }

  constructor(opts: OceanOptions = {}) {
    this.n = opts.n ?? 256;
    this.count = Math.max(1, Math.min(3, opts.cascades ?? 3));
    const n = this.n, W = n * this.count;
    const fl = { type: THREE.FloatType, format: THREE.RGBAFormat, depthBuffer: false, stencilBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false };
    this.h0 = new THREE.WebGLRenderTarget(W, n, fl);
    this.pp = [makeMrt(W, n, fl), makeMrt(W, n, fl)];
    this.radices = fftRadices(n);
    const rt = (type: THREE.TextureDataType) => {
      const t = new THREE.WebGLRenderTarget(n, n, {
        type, format: THREE.RGBAFormat, depthBuffer: false, stencilBuffer: false,
        minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter,
        generateMipmaps: true, wrapS: THREE.RepeatWrapping, wrapT: THREE.RepeatWrapping,
      });
      t.texture.anisotropy = 8;
      return t;
    };
    for (let c = 0; c < this.count; c++) {
      this.casc.push({ L: CASCADE_L[c]!, A: rt(THREE.HalfFloatType), B: [rt(THREE.HalfFloatType), rt(THREE.HalfFloatType)], flip: 0 });
    }
    this.tw = new THREE.DataTexture(buildTwiddles(n), n, 1, THREE.RGBAFormat, THREE.FloatType);
    this.tw.minFilter = this.tw.magFilter = THREE.NearestFilter; this.tw.generateMipmaps = false; this.tw.needsUpdate = true;

    const mat = (frag: string, uniforms: Record<string, THREE.IUniform>, glsl3 = false) => new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: frag, uniforms, depthTest: false, depthWrite: false, blending: THREE.NoBlending,
      ...(glsl3 ? { glslVersion: THREE.GLSL3 } : {}),     // the multiple-target passes declare their own outputs
    });
    const u = (v: any) => ({ value: v });
    const Ls = CASCADE_L.slice(0, 3);
    this.mH0 = mat(H0_FRAG, { uN: u(n), uLs: u(Ls.slice()), uGains: u([1, 1, 1]), uSpeed: u(6), uDir: u(0), uFetch: u(100000), uDepth: u(3), uRipple: u(RIPPLE_GAIN),
      uEdge: u(new THREE.Vector2(BAND_EDGE[0], BAND_EDGE[1])), uWidth: u(BAND_WIDTH), uKTop: u(K_TOP),
      uSwN: u(0), uSwA: u([new THREE.Vector4(), new THREE.Vector4()]), uSwV: u([0, 0]) });
    this.mEvolve = mat(EVOLVE_FRAG, { uH0: u(null), uN: u(n), uLs: u(Ls.slice()), uT: u(0), uDepth: u(3) }, true);
    this.mFFT = mat(FFT_FRAG, { uIn0: u(null), uIn1: u(null), uTw: u(this.tw), uN: u(n), uNs: u(1), uR: u(4), uVert: u(0) }, true);
    this.mAsm = mat(ASSEMBLE_FRAG, { uFA: u(null), uFB: u(null), uFoam: u(null), uTileX: u(0), uMode: u(0), uLambda: u(1), uFoamBias: u(0.6), uFoamDecay: u(1), uFoamGain: u(1), uFoamOn: u(1) });
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mH0);
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
  }

  /** The current sea state (a copy). */
  get seaState(): SeaState { return { ...this.state }; }

  /** Change the sea state. The spectrum is rebuilt at the next update when anything moved. */
  setState(s: SeaState) {
    const o = this.state;
    if (Math.abs(s.speed - o.speed) > 0.02 || Math.abs(s.dir - o.dir) > 0.002 || Math.abs(s.fetch - o.fetch) > 1 || Math.abs(s.depth - o.depth) > 0.01 || swellMoved(s.swell, o.swell)) {
      this.state = { ...s, ...(s.swell ? { swell: s.swell.map(t => ({ ...t })) } : {}) }; this.dirty = true;
    }
  }

  /** Slope/height texture of tile c (rgba: slope x, slope z, height, Jacobian), mip-mapped, repeating. */
  texA(c: number): THREE.Texture { return this.casc[c]!.A.texture; }
  /** Second-moment/foam texture of tile c (rgba: sx^2, sz^2, sx*sz, foam). */
  texB(c: number): THREE.Texture { const k = this.casc[c]!; return k.B[k.flip]!.texture; }

  private draw(renderer: THREE.WebGLRenderer, m: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget) {
    this.quad.material = m;
    renderer.setRenderTarget(target);
    renderer.render(this.scene, this.cam);
  }

  /** Run the passes for time `tSec` (any monotone clock in seconds) and step `dt`. Call once per frame, outside the main render.
   *  Draws: 1 (spectrum, only when the sea state moved) + 1 evolve + 2 * (FFT stages per axis) + 2 per tile. */
  update(renderer: THREE.WebGLRenderer, tSec: number, dt: number) {
    const prevTarget = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear, prevXr = renderer.xr.enabled, prevShadow = renderer.shadowMap.autoUpdate;
    const prevReset = renderer.info.autoReset;
    renderer.autoClear = false; renderer.xr.enabled = false; renderer.shadowMap.autoUpdate = false; renderer.info.autoReset = false;
    const prevClear = renderer.getClearColor(new THREE.Color()), prevClearA = renderer.getClearAlpha();
    const n = this.n;
    const t = ((tSec % T_WRAP) + T_WRAP) % T_WRAP;
    const step = this.lastT < 0 ? 0.016 : Math.min(0.1, Math.max(0, dt));
    this.lastT = tSec;
    const s = this.state;

    if (this.dirty) {
      const U = this.mH0.uniforms;
      U.uSpeed!.value = s.speed; U.uDir!.value = s.dir; U.uFetch!.value = s.fetch; U.uDepth!.value = s.depth; U.uRipple!.value = this.rippleGain;
      for (let c = 0; c < 3; c++) { U.uLs!.value[c] = CASCADE_L[c]; U.uGains!.value[c] = this.gain[c] ?? 1; }
      const sw = swellParams(s);
      U.uSwN!.value = sw.length;
      for (let i = 0; i < MAX_SWELL; i++) {
        const p = sw[i];
        (U.uSwA!.value[i] as THREE.Vector4).set(p?.wp ?? 1, p?.sig ?? 1, p?.dir ?? 0, p?.sp ?? SWELL_S);
        U.uSwV!.value[i] = p?.variance ?? 0;
      }
      this.draw(renderer, this.mH0, this.h0);
      this.dirty = false;
    }

    // evolve every tile's spectrum to time t and pack the fields: both packs in one pass, into the first of the ping-pong pair
    const E = this.mEvolve.uniforms;
    E.uH0!.value = this.h0.texture; E.uT!.value = t; E.uDepth!.value = s.depth;
    for (let c = 0; c < 3; c++) E.uLs!.value[c] = CASCADE_L[c];
    this.draw(renderer, this.mEvolve, this.pp[0]);

    // inverse FFT of both packs, all tiles at once: along x, then along y; each pass reads one of the pair and writes the other
    const F = this.mFFT.uniforms;
    let cur = 0;
    for (let vert = 0; vert < 2; vert++) {
      let ns = 1;
      for (const r of this.radices) {
        const [i0, i1] = mrtTextures(this.pp[cur]!);
        F.uIn0!.value = i0; F.uIn1!.value = i1; F.uNs!.value = ns; F.uR!.value = r; F.uVert!.value = vert;
        this.draw(renderer, this.mFFT, this.pp[1 - cur]!);
        cur = 1 - cur; ns *= r;
      }
    }

    // pack the results of each tile into its textures
    const [fa, fb] = mrtTextures(this.pp[cur]!);
    const A_ = this.mAsm.uniforms;
    A_.uFA!.value = fa; A_.uFB!.value = fb; A_.uLambda!.value = this.lambda;
    A_.uFoamBias!.value = this.foamBias; A_.uFoamGain!.value = this.foamGain; A_.uFoamOn!.value = this.foamOn;
    A_.uFoamDecay!.value = Math.exp(-step / Math.max(this.foamDecay, 0.05));
    for (let c = 0; c < this.count; c++) {
      const k = this.casc[c]!;
      const prevB = k.B[k.flip]!, nextB = k.B[1 - k.flip]!;
      A_.uTileX!.value = c * n;
      A_.uFoam!.value = prevB.texture;
      A_.uMode!.value = 0; this.draw(renderer, this.mAsm, k.A);
      A_.uMode!.value = 1; this.draw(renderer, this.mAsm, nextB);
      k.flip = 1 - k.flip;
    }

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAuto; renderer.xr.enabled = prevXr; renderer.shadowMap.autoUpdate = prevShadow; renderer.info.autoReset = prevReset;
    renderer.setClearColor(prevClear, prevClearA);
  }

  /** Test helper: the height field of tile c at texel resolution, in metres (reads the GPU back; slow). */
  readHeights(renderer: THREE.WebGLRenderer, c: number): Float32Array {
    const n = this.n, buf = new Uint16Array(n * n * 4);
    renderer.readRenderTargetPixels(this.casc[c]!.A, 0, 0, n, n, buf as any);
    const out = new Float32Array(n * n);
    for (let i = 0; i < n * n; i++) out[i] = THREE.DataUtils.fromHalfFloat(buf[i * 4 + 2]!);
    return out;
  }
  /** Test helper: the slope x, slope z, height and Jacobian channels of tile c. */
  readA(renderer: THREE.WebGLRenderer, c: number): Float32Array {
    const n = this.n, buf = new Uint16Array(n * n * 4);
    renderer.readRenderTargetPixels(this.casc[c]!.A, 0, 0, n, n, buf as any);
    const out = new Float32Array(n * n * 4);
    for (let i = 0; i < n * n * 4; i++) out[i] = THREE.DataUtils.fromHalfFloat(buf[i]!);
    return out;
  }
  /** Test helper: the foam channel of tile c. */
  readFoam(renderer: THREE.WebGLRenderer, c: number): Float32Array {
    const n = this.n, k = this.casc[c]!, buf = new Uint16Array(n * n * 4);
    renderer.readRenderTargetPixels(k.B[k.flip]!, 0, 0, n, n, buf as any);
    const out = new Float32Array(n * n);
    for (let i = 0; i < n * n; i++) out[i] = THREE.DataUtils.fromHalfFloat(buf[i * 4 + 3]!);
    return out;
  }
  /** Test helper: the second-moment channels (sx^2, sz^2, sx*sz, foam) of tile c. */
  readB(renderer: THREE.WebGLRenderer, c: number): Float32Array {
    const n = this.n, k = this.casc[c]!, buf = new Uint16Array(n * n * 4);
    renderer.readRenderTargetPixels(k.B[k.flip]!, 0, 0, n, n, buf as any);
    const out = new Float32Array(n * n * 4);
    for (let i = 0; i < n * n * 4; i++) out[i] = THREE.DataUtils.fromHalfFloat(buf[i]!);
    return out;
  }

  dispose() {
    for (const k of this.casc) { k.A.dispose(); k.B[0].dispose(); k.B[1].dispose(); }
    this.h0.dispose(); this.pp[0].dispose(); this.pp[1].dispose();
    this.tw.dispose(); this.quad.geometry.dispose();
    this.mH0.dispose(); this.mEvolve.dispose(); this.mFFT.dispose(); this.mAsm.dispose();
  }
}
